import get from "lodash/get";
import {forwardRef, useEffect, useImperativeHandle, useMemo, useState} from "react";
import type {ReactElement, ReactNode, Ref as ReactRef} from "react";

import {memoWithType} from "../../react";

import useForm, {FormContext} from "./useForm";
import {getFirstPath} from "./utils";

type Props<TFieldValues> = {
    errors: Partial<Record<keyof TFieldValues, unknown>>;
    children: ReactNode;
    submitCount: number;
    autoScrollToErrors?: boolean;
};

// we can't infer the type that has generic as argument
export type Ref<TFieldValues> = {
    scrollToField: (name: keyof TFieldValues) => void;
};

const Form = forwardRef(function Form<
    TFieldValues extends Record<string, any> = Record<string, any>
>(
    {errors: errorsProp, children, submitCount, autoScrollToErrors = true}: Props<TFieldValues>,
    ref: ReactRef<Ref<Record<string, any>>>
) {
    const [errors, setErrors] = useState(errorsProp);
    const {
        scrollToField,
        scrollToElement,
        setItemRef,
        getItemRef,
        setFieldGroup,
        setNavigateToGroup
    } = useForm<TFieldValues>();

    // auto scroll to the one which contains the error
    useEffect(() => {
        if (!autoScrollToErrors) {
            return;
        }
        const firstPath = getFirstPath(errors) as keyof TFieldValues | undefined;
        if (firstPath) {
            const errorValue = get(errors, firstPath);
            if (errorValue instanceof Object && "ref" in errorValue) {
                const {ref} = errorValue;
                if (ref && ref instanceof HTMLElement) {
                    scrollToElement(firstPath, ref);
                    return;
                }
            }
            scrollToField(firstPath);
        }
    }, [autoScrollToErrors, errors, submitCount, scrollToField, scrollToElement]);

    useEffect(() => {
        setErrors(errorsProp);
    }, [errorsProp, submitCount]);

    useImperativeHandle(ref, () => ({scrollToField}), [scrollToField]);

    return (
        <FormContext.Provider
            value={useMemo(
                () => ({
                    setItemRef,
                    getItemRef,
                    setFieldGroup,
                    setNavigateToGroup,
                    scrollToField,
                    setErrors
                }),
                [setItemRef, getItemRef, setFieldGroup, setNavigateToGroup, scrollToField]
            )}
        >
            {children}
        </FormContext.Provider>
    );
}) as <TFieldValues>(
    props: Props<TFieldValues> & {ref?: ReactRef<Ref<TFieldValues>>}
) => ReactElement;

export default memoWithType(Form);
